import { dequal } from 'dequal';
import { useState, useMemo } from 'react';

// Lib
import { SIZE_FULL } from '@/lib/gridElements/size';

// Utils
import {
    getChartCumulativeViewData,
    getChartComparativeViewData,
    getChartComparativeViewConfig
} from '@/utils/groups/analytics/getChartData';

// Hooks
import useUser from '@/utils/hooks/swr/useUser';

// Layout
import AnalyticsGrid from '@/components/layouts/wrappers/AnalyticsGrid';

// Components
import GroupGlobalTable from '@/components/pages/analyze-group/tables/GroupGlobalTable';

export const RenderGroupAnalyticsItem = ({
    group,
    config,
    analytics,
    periodSelected,
    analyticsState,
    mostEfficientSocialProfiles,
    socialProfilesDisplayInfos,
    ...props
}) => {
    const { user } = useUser({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });
    const userDataFormat = {
        numbers_format: user.settings.numbers_format,
        date_format: user.settings.date_format,
        timezone: user.settings.timezone,
        time_format: user?.settings?.time_format
    };

    const [isComparativeViewActive, setIsComparativeViewActive] = useState(false);

    const data = useMemo(() => {
        if (config?.type !== 'chart') {
            return {};
        }
        const formattedConfig = isComparativeViewActive
            ? getChartComparativeViewConfig(
                  mostEfficientSocialProfiles,
                  config,
                  socialProfilesDisplayInfos
              )
            : config;

        if (!analytics) {
            return { formattedData: {}, formattedConfig };
        }
        return {
            formattedData: isComparativeViewActive
                ? getChartComparativeViewData(analytics, mostEfficientSocialProfiles, config)
                : getChartCumulativeViewData(analytics, group?.id, config),
            formattedConfig
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isComparativeViewActive, analytics]);

    switch (config.type) {
        case 'groupGlobalTable':
            return (
                <AnalyticsGrid.Item size={SIZE_FULL} key={config.id}>
                    <GroupGlobalTable
                        config={config}
                        periodSelected={periodSelected}
                        userDataFormat={userDataFormat}
                    />
                </AnalyticsGrid.Item>
            );
        case 'chart':
            return (
                <AnalyticsGrid.Item size={config.size} key={config.id}>
                    <config.element
                        {...props}
                        onReset={analyticsState?.mutateAnalytics}
                        isLoading={
                            analyticsState?.isLoadingAnalytics ||
                            (!analyticsState?.isLoadingAnalytics &&
                                analytics &&
                                dequal(data?.formattedData, {}))
                        }
                        periodData={{
                            periodStart: periodSelected.start,
                            periodEnd: periodSelected.end,
                            period: periodSelected.period,
                            compareTo: periodSelected.compareTo
                        }}
                        setRequestParams={analyticsState?.setRequestParams}
                        setState={analyticsState?.setState}
                        data={
                            !analyticsState?.isLoadingAnalytics && analytics
                                ? {
                                      ...data?.formattedConfig,
                                      ...data?.formattedData
                                  }
                                : config
                        }
                        userNumbersFormat={{
                            number: user?.settings?.numbers_format ?? 'comma',
                            date: {
                                timezone: user?.settings?.timezone,
                                format: user?.settings?.date_format
                            }
                        }}
                        isComparativeViewActive={isComparativeViewActive}
                        toggleComparativeView={() => {
                            setIsComparativeViewActive((prev) => !prev);
                        }}
                    />
                </AnalyticsGrid.Item>
            );
        case 'widget':
            return (
                <AnalyticsGrid.Item size={config.size} key={config.id}>
                    <config.element
                        {...props}
                        config={config}
                        sortFields={config.sortFields}
                        page={config.page}
                        numberFormat={user?.settings?.numbers_format ?? 'comma'}
                        periodData={periodSelected}
                    />
                </AnalyticsGrid.Item>
            );
        default:
            return null;
    }
};
